import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { paidToast, depositState, DepositProof } from '../components/AdminPayments';
import { CancelBooking } from '../components/CancelBooking';
import { RunningLate, LateBadge, useLateness } from '../components/RunningLate';
import { BookingSearch } from '../components/BookingSearch';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ChevronLeft, ChevronRight, Calendar as CalIcon, LayoutGrid, ArrowLeft, RefreshCw, Phone, Mail, MapPin, Truck, X as XIcon, AlertTriangle, Plus, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import LoginForm from '../auth/LoginForm';
import { JobDetailsEditor } from '../components/JobDetailsEditor';
import { JobInventoryEditor } from '../components/admin/JobInventoryEditor';
import { JobInventoryFields } from '../components/admin/JobInventoryFields';
import { EMPTY_INV, jobKind, SERVICE_OPTIONS, serviceLabel } from '../components/admin/inventoryUtils';
import { JobTypeEditor } from '../components/admin/JobTypeEditor';
import { CustomerEditor } from '../components/CustomerEditor';
import { RescheduleEditor } from '../components/RescheduleEditor';
import { MonthPicker } from '../components/MonthPicker';
import { AddressFinder } from '../components/AddressFinder';
import { JobAddressLinks } from '../components/AddressLinks';
import { StatusButtons } from '../components/driver/StatusButtons';
import { ProofPanel } from '../components/driver/ProofPanel';
import { BalancePanel } from '../components/driver/BalancePanel';
import { ReportActions } from '../components/driver/ReportActions';
import { JobCrewSummary } from '../components/porters/JobCrewSummary';
import { withOverride, errMsg } from '../components/porters/porterUi';
import { JobPhotos, uploadJobPhotos } from '../components/JobPhotos';
import { DepositEditor } from '../components/DepositEditor';
import { TravelLegs, DayMiles, useDayMiles, addrLine } from '../components/travel/TravelLegs';

const STATUS_BADGE = {
  on_the_way: { label: 'On the way', cls: 'bg-sky-600' },
  arrived: { label: 'Arrived', cls: 'bg-amber-600' },
  done: { label: 'Done', cls: 'bg-emerald-600' },
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const resolveAvatar = (p) => (!p ? '' : /^https?:/.test(p) ? p : `${BACKEND}${p}`);
const DriverChip = ({ d, size = 20 }) => {
  const px = `${size}px`;
  if (d?.avatar_url) {
    return (
      <img
        src={resolveAvatar(d.avatar_url)}
        alt={d.name}
        className="rounded-full object-cover shrink-0 border"
        style={{ width: px, height: px, borderColor: d.color }}
      />
    );
  }
  return <span className="rounded-full shrink-0" style={{ width: px, height: px, background: d?.color || '#64748b' }} />;
};

const START_HOUR = 6;
const END_HOUR = 23;
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);
const SLOT_HEIGHT_PX = 88; // 1 hour row height (half-hour job = 44px, fits the compact card)

const toYMD = (d) => {
  // Local-time YYYY-MM-DD (avoids UTC-shift bugs when a user is in a timezone
  // where toISOString() would roll back the date)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const parseYMD = (s) => new Date(s + 'T00:00:00');
const fmtDay = (d) => d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
const parseHM = (t) => {
  if (!t) return { h: 0, m: 0 };
  const [h, m] = t.split(':').map(x => parseInt(x, 10));
  return { h: h || 0, m: m || 0 };
};
const hmToMin = (t) => { const { h, m } = parseHM(t); return h * 60 + m; };
const minToHM = (totalMin) => {
  const h = Math.max(0, Math.min(23, Math.floor(totalMin / 60)));
  const m = Math.max(0, Math.min(59, totalMin % 60));
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
const durationHours = (hrsEstimate) => {
  if (!hrsEstimate) return 2;
  const m = String(hrsEstimate).match(/(\d+(?:\.\d+)?)/);
  return Math.max(0.5, Math.min(16, m ? parseFloat(m[1]) : 2));
};
const calStart = (b) => hmToMin(b.booking.time);
const calDur = (b) => durationHours(b.hours_estimate) * 60;
const calPx = (m) => ((m - START_HOUR * 60) / 60) * SLOT_HEIGHT_PX;

// Detect waste/clearance jobs so we can colour-code them
const isWasteJob = (item) => {
  const haystack = item?.service ? item.service : [item?.van_recommendation, item?.property_size]
    .filter(Boolean).join(' ').toLowerCase();
  return /clearance|waste|rubbish/.test(haystack);
};
const WASTE_BG = '#7c3aed'; // violet-600
const AB_BG = '#fbbf24';   // amber-400

/* -------- Quick-detail modal (lightweight; links to full dashboard) -------- */
const QuickModal = ({ item, onClose, drivers, onAction, authFetch }) => {
  const [staffNotes, setStaffNotes] = useState(item?.staff_notes || '');
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const lateMap = useLateness(authFetch, item?.booking?.date, `${item?.id}${item?.running_late || 0}`);
  if (!item) return null;
  const d = drivers.find(x => x.id === item.driver_id);
  const dep = depositState(item);
  const paid = dep === 'verified';
  const act = async (fn, label) => {
    try { await fn(); toast.success(label); onAction?.(); onClose(); }
    catch { toast.error('Failed.'); }
  };
  const toggleDeposit = async () => {
    try {
      if (paid) {
        await authFetch('post', `/admin/estimate/${item.id}/mark-unpaid`);
        toast.success('Deposit marked unpaid.');
      } else {
        if (!window.confirm(`Mark the £${item.deposit} deposit as paid? ${item.email ? 'The customer will get a "booking confirmed" email.' : 'No customer email on file.'}`)) return;
        const res = await authFetch('post', `/admin/estimate/${item.id}/mark-paid`);
        toast.success(`Deposit ${paidToast(res?.data)}`);
      }
      onAction?.();
      onClose();
    } catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : "Couldn't update."); }
  };
  const sendBookingEmail = async () => {
    if (!window.confirm(paid ? 'Send the "booking confirmed" email to the customer?' : `Send the booking email? It will say the booking is NOT fully confirmed until the £${item.deposit} deposit is paid, with the Tide link and their reference.`)) return;
    setSendingEmail(true);
    try {
      const { data } = await authFetch('post', `/admin/estimate/${item.id}/send-booking-email`);
      toast.success(data.kind === 'confirmed' ? 'Booking confirmed email sent.' : 'Booking email sent (not yet confirmed, deposit due).');
      onAction?.();
    } catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : "Couldn't send the email."); }
    finally { setSendingEmail(false); }
  };
  const saveNotes = async () => {
    if (savingNotes) return;
    setSavingNotes(true);
    try {
      await authFetch('patch', `/admin/estimate/${item.id}/staff-notes`, { notes: staffNotes });
      toast.success('Staff notes saved.');
      setEditingNotes(false);
      onAction?.();
    } catch { toast.error("Couldn't save notes."); }
    finally { setSavingNotes(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="cal-quick-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-xl shadow-xl p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)]">#{item.ref} · {item.name || '—'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="space-y-2 text-sm text-slate-700">
          <div className="flex items-center gap-2" data-testid="cal-modal-when"><CalIcon className="w-4 h-4 text-[color:var(--mv-orange)]" /> <strong>{item.booking?.date}</strong> at <strong>{item.booking?.time}</strong> · {item.hours_estimate || '2 hours'}</div>
          {(item.customer_profile?.booked_on || Object.keys(CUSTOMER_TAGS).some(k => tagOf(item, k))) && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="cal-modal-booked-on">
              {item.customer_profile?.booked_on && <span className="text-slate-500 mr-1">Booked in on <strong className="text-slate-700">{new Date(item.customer_profile.booked_on + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</strong></span>}
              {Object.keys(CUSTOMER_TAGS).map(k => tagOf(item, k) && (
                <span key={k} className={`font-black uppercase text-[10px] px-1.5 py-0.5 rounded border ${tagOf(item, k).cls}`} data-testid={`cal-modal-tag-${k}`}>{tagOf(item, k).label}</span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-slate-500" /> {item.van_recommendation || serviceLabel(item.service) || '—'}</div>
          <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-500" /> {item.from_postcode || '—'} → {item.to_postcode || '—'}</div>
          <div className="pl-6"><JobAddressLinks job={item} testid="cal-modal-addresses" /></div>
          {item.phone && <a className="flex items-center gap-2 text-[color:var(--mv-orange)]" href={`tel:${item.phone}`}><Phone className="w-4 h-4" />{item.phone}</a>}
          {item.email && <a className="flex items-center gap-2 text-[color:var(--mv-orange)]" href={`mailto:${item.email}`}><Mail className="w-4 h-4" />{item.email}</a>}
          <div className="flex items-center gap-2 text-xs text-slate-500">
            Driver: {d ? <span className="inline-flex items-center gap-1.5 font-black" style={{ color: d.color }}><DriverChip d={d} size={16} />{d.name}</span> : <span className="text-amber-700 font-bold">Unassigned</span>}
          </div>
          <RescheduleEditor key={`r-${item.id}`} item={item} drivers={drivers} authFetch={authFetch} onSaved={() => { onAction(); onClose(); }} />
          <CustomerEditor key={item.id} item={item} authFetch={authFetch} onSaved={onAction} />
          <JobTypeEditor key={`jt-${item.id}-${item.service}`} job={item} authFetch={authFetch} onSaved={onAction} />
          <JobDetailsEditor key={`jd-${item.id}-${item.hours_estimate}-${item.booking?.final_price}`} item={item} authFetch={authFetch} onSaved={onAction} />
          <JobInventoryEditor key={`inv-${item.id}`} job={item} authFetch={authFetch} onSaved={onAction} />
          <JobPhotos job={item} authFetch={authFetch} onUpdated={onAction} canEdit showCustomer />

          {/* Deposit status — tappable to toggle */}
          <button
            onClick={toggleDeposit}
            className={`w-full mt-2 py-2 rounded-md font-black text-sm inline-flex items-center justify-center gap-2 border-2 transition ${paid ? 'bg-emerald-50 border-emerald-500 text-emerald-800 hover:bg-emerald-100' : dep === 'claimed' ? 'bg-amber-50 border-amber-500 text-amber-900 hover:bg-amber-100' : 'bg-red-50 border-red-500 text-red-800 hover:bg-red-100'}`}
            data-testid="cal-deposit-toggle"
          >
            {paid ? (<><CheckCircle className="w-5 h-5" /> DEPOSIT PAID (CHECKED) · tap to unmark</>)
              : dep === 'claimed' ? (<><AlertTriangle className="w-5 h-5" /> CONFIRM BOOKING DEPOSIT · tap once checked in Tide to confirm & email</>)
              : (<><XCircle className="w-5 h-5" /> DEPOSIT NOT PAID · tap when customer has paid to confirm & email</>)}
          </button>
          <button type="button" onClick={sendBookingEmail} disabled={sendingEmail || !item.email} className="w-full mt-2 py-2 rounded-md font-bold text-sm inline-flex items-center justify-center gap-2 border border-[color:var(--mv-navy)] text-[color:var(--mv-navy)] hover:bg-slate-50 disabled:opacity-50" data-testid="cal-send-booking-email">
            <Mail className="w-4 h-4" /> {sendingEmail ? 'Sending…' : !item.email ? 'No customer email on file' : paid ? 'Email "booking confirmed" to customer' : 'Email booking details (not yet confirmed, deposit due)'}
          </button>
          {item.driver_id && !item.completed_at && <RunningLate job={item} authFetch={authFetch} projection={lateMap[item.id]} onSaved={() => onAction?.()} />}
          <CancelBooking job={item} authFetch={authFetch} onDone={() => { onAction?.(); onClose(); }} />
          {item.last_booking_email && <div className="text-[11px] text-slate-500 mt-1 text-center" data-testid="cal-last-booking-email">Last booking email: {item.last_booking_email.kind === 'confirmed' ? 'confirmed' : 'not yet confirmed'} · {new Date(item.last_booking_email.at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</div>}
          <DepositProof item={item} />

          {/* Staff notes */}
          <div className="mt-3 rounded-md bg-indigo-50 border border-indigo-200 p-2">
            <div className="flex items-center justify-between mb-1">
              <strong className="text-[10px] font-black uppercase tracking-wider text-indigo-800">🔒 Staff note (internal)</strong>
              {!editingNotes && <button onClick={() => setEditingNotes(true)} className="text-[11px] font-bold text-indigo-700" data-testid="cal-staff-notes-edit">Edit</button>}
            </div>
            {editingNotes ? (
              <>
                <textarea value={staffNotes} onChange={(e) => setStaffNotes(e.target.value)} rows={3} maxLength={2000} className="w-full text-xs rounded border border-indigo-200 bg-white px-2 py-1.5" data-testid="cal-staff-notes-textarea" />
                <div className="mt-1 flex items-center justify-end gap-2">
                  <button onClick={() => { setStaffNotes(item.staff_notes || ''); setEditingNotes(false); }} className="text-[11px] font-bold text-slate-500">Cancel</button>
                  <button onClick={saveNotes} disabled={savingNotes} className="text-[11px] font-bold bg-indigo-600 text-white px-3 py-1 rounded" data-testid="cal-staff-notes-save">{savingNotes ? '…' : 'Save'}</button>
                </div>
              </>
            ) : (
              <div className="text-xs text-indigo-900 whitespace-pre-wrap min-h-[18px]">{staffNotes || <span className="text-indigo-400 italic">No staff notes yet — tap Edit to add.</span>}</div>
            )}
          </div>

          <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
            <div className="font-display text-2xl font-black text-[color:var(--mv-navy)]" data-testid="cal-modal-price">£{item.booking?.final_price ?? item.price}</div>
            <DepositEditor key={`${item.id}-${item.deposit}`} item={item} authFetch={authFetch} onSaved={onAction} />
          </div>
          <JobCrewSummary job={item} authFetch={authFetch} onChanged={onAction} />
          <div className="pt-3 border-t border-slate-100 space-y-2" data-testid="cal-modal-onday">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">On the day</div>
            <StatusButtons job={item} authFetch={authFetch} onUpdated={onAction} />
            {item.job_status_history?.length > 0 && (
              <ul className="text-[11px] text-slate-500 space-y-0.5" data-testid="cal-status-history">
                {item.job_status_history.map((h, i) => (
                  <li key={i}>{STATUS_BADGE[h.status]?.label || h.status} · {new Date(h.at + (h.at.endsWith('Z') || h.at.includes('+') ? '' : 'Z')).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} · {h.by}</li>
                ))}
              </ul>
            )}
            <BalancePanel key={`${item.id}-${item.deposit}-${item.deposit_claimed_at}`} job={item} authFetch={authFetch} onUpdated={onAction} />
            <ProofPanel job={item} authFetch={authFetch} onUpdated={onAction} />
            <ReportActions job={item} authFetch={authFetch} onUpdated={onAction} />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          {!item.completed_at && (
            <button onClick={() => act(() => authFetch('post', `/admin/estimate/${item.id}/complete`), 'Completed.')} className="px-3 h-9 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold">Mark done</button>
          )}
          <Link to="/admin" state={{ openQuote: item.id }} className="px-3 h-9 rounded-md bg-[color:var(--mv-navy)] hover:bg-[#13315c] text-white text-xs font-bold inline-flex items-center">Open in dashboard →</Link>
        </div>
      </div>
    </div>
  );
};

/* -------- One day's grid (drivers columns × hour rows) -------- */
const DayGrid = ({ date, drivers, bookings, onDrop, onBlockClick }) => {
  const [touchDrag, setTouchDrag] = useState(null); // { id, x, y } for visual feedback
  const [dragId, setDragId] = useState(null);
  const [preview, setPreview] = useState(null); // { col, top, height, time }
  const grab = useRef({ offset: 0, dur: 60 });
  const slotFor = (colEl, clientY) => {
    const rect = colEl.getBoundingClientRect();
    const y = clientY - rect.top - grab.current.offset;
    const maxMin = HOURS.length * 60 - 15;
    const snapped = Math.min(maxMin, Math.max(0, Math.round(((y / SLOT_HEIGHT_PX) * 60) / 15) * 15));
    return { snapped, time: minToHM(START_HOUR * 60 + snapped), top: (snapped / 60) * SLOT_HEIGHT_PX };
  };
  const showPreview = (col, slot) => setPreview(p => (p && p.col === col && p.time === slot.time ? p : { col, top: slot.top, time: slot.time, height: Math.max(40, (grab.current.dur / 60) * SLOT_HEIGHT_PX - 2) }));
  const endDrag = () => { setDragId(null); setPreview(null); };

  // Mobile touch-drag: pointer events chain works on iOS/Android for a floating preview.
  // Touch target is resolved on touchend using elementFromPoint() → finds the column + y offset.
  const startTouchDrag = (e, id, dur) => {
    // Only trigger if the user touches and holds briefly — distinguish from a tap
    const t = e.touches[0];
    grab.current = { offset: t.clientY - e.currentTarget.getBoundingClientRect().top, dur };
    const startX = t.clientX;
    const startY = t.clientY;
    let started = false;
    const onMove = (ev) => {
      const tm = ev.touches[0];
      if (!tm) return;
      if (!started) {
        const dx = Math.abs(tm.clientX - startX);
        const dy = Math.abs(tm.clientY - startY);
        if (dx + dy > 12) {
          started = true;
          ev.preventDefault();
        } else {
          return;
        }
      }
      ev.preventDefault();
      setTouchDrag({ id, x: tm.clientX, y: tm.clientY });
      setDragId(id);
      let node = document.elementFromPoint(tm.clientX, tm.clientY);
      while (node && !node.dataset?.calCol) node = node.parentElement;
      if (node) showPreview(node.dataset.calCol, slotFor(node, tm.clientY));
      else setPreview(null);
    };
    const onEnd = (ev) => {
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
      setTouchDrag(null);
      endDrag();
      if (!started) return; // treat as a tap, don't trigger drop (click handler fires separately)
      const te = ev.changedTouches[0];
      if (!te) return;
      const el = document.elementFromPoint(te.clientX, te.clientY);
      // Walk up to find a column with data-cal-col attribute
      let node = el;
      while (node && !node.dataset?.calCol) node = node.parentElement;
      if (!node) return;
      const driverId = node.dataset.calCol === 'null' ? null : node.dataset.calCol;
      onDrop(id, { driverId, date, time: slotFor(node, te.clientY).time });
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
  };

  const bookingsForDay = bookings.filter(b => b.booking?.date === date);
  const dayIsEmpty = bookingsForDay.length === 0;
  const daySig = bookingsForDay.map(b => `${b.id}${b.driver_id}${b.booking?.time}${b.from_postcode}${b.to_postcode}`).join(',');
  const { authFetch: lateFetch } = useAuth();
  const lateMap = useLateness(lateFetch, date, daySig + bookingsForDay.map(b => b.running_late || 0).join(','));
  const dm = useDayMiles(date, 1, daySig);
  const dayMiles = dm.totals[date] || {};
  const hasUnassigned = bookingsForDay.some(b => !b.driver_id);
  const cols = useMemo(() => (hasUnassigned ? [{ id: null, name: 'Unassigned', color: '#94a3b8' }, ...drivers] : drivers), [drivers, hasUnassigned]);

  // Group bookings by driver_id (or null)
  const byDriver = useMemo(() => {
    const m = new Map();
    cols.forEach(c => m.set(c.id || '__u', []));
    bookingsForDay.forEach(b => {
      const k = b.driver_id || '__u';
      if (m.has(k)) m.get(k).push(b);
      else m.set(k, [b]);
    });
    return m;
  }, [bookingsForDay, cols]);

  // Detect overlaps per driver (same column, time ranges touch)
  const overlapMap = useMemo(() => {
    const o = new Map();
    for (const [key, list] of byDriver.entries()) {
      list.sort((a, b) => hmToMin(a.booking.time) - hmToMin(b.booking.time));
      for (let i = 0; i < list.length; i++) {
        const a = list[i];
        const aStart = hmToMin(a.booking.time);
        const aEnd = aStart + durationHours(a.hours_estimate) * 60;
        for (let j = i + 1; j < list.length; j++) {
          const b = list[j];
          const bStart = hmToMin(b.booking.time);
          if (bStart < aEnd) { o.set(a.id, true); o.set(b.id, true); }
        }
      }
    }
    return o;
  }, [byDriver]);

  // Side-by-side lanes for clashing blocks so none get hidden underneath
  const laneMap = useMemo(() => {
    const lanes = new Map();
    for (const list of byDriver.values()) {
      const ends = [];
      const sorted = [...list].sort((a, b) => hmToMin(a.booking.time) - hmToMin(b.booking.time));
      const groupIds = [];
      let groupEnd = -1;
      const flush = () => { const n = Math.max(...groupIds.map(id => lanes.get(id).lane)) + 1; groupIds.forEach(id => { lanes.get(id).count = n; }); groupIds.length = 0; ends.length = 0; };
      for (const b of sorted) {
        const s0 = hmToMin(b.booking.time);
        const e0 = s0 + durationHours(b.hours_estimate) * 60;
        if (groupIds.length && s0 >= groupEnd) flush();
        let lane = ends.findIndex(e => e <= s0);
        if (lane === -1) { lane = ends.length; ends.push(e0); } else ends[lane] = e0;
        lanes.set(b.id, { lane, count: 1 });
        groupIds.push(b.id);
        groupEnd = Math.max(groupEnd, e0);
      }
      if (groupIds.length) flush();
    }
    return lanes;
  }, [byDriver]);

  const gridRows = HOURS.length; // 18

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    showPreview(e.currentTarget.dataset.calCol, slotFor(e.currentTarget, e.clientY));
  };
  const handleDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setPreview(null); };

  const handleCellDrop = (e, driverId) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    const { time } = slotFor(e.currentTarget, e.clientY);
    endDrag();
    if (!id) return;
    onDrop(id, { driverId, date, time });
  };
  const handleDragStart = (e, b, dur) => {
    e.dataTransfer.setData('text/plain', b.id);
    e.dataTransfer.effectAllowed = 'move';
    grab.current = { offset: e.clientY - e.currentTarget.getBoundingClientRect().top, dur };
    try { e.dataTransfer.setDragImage(e.currentTarget, e.nativeEvent.offsetX, grab.current.offset); } catch (_) {}
    setTimeout(() => setDragId(b.id), 0);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`cal-day-${date}`}>
      {touchDrag && (
        <div className="fixed pointer-events-none z-[60] -translate-x-1/2 -translate-y-1/2 bg-[color:var(--mv-navy)] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg" style={{ left: touchDrag.x, top: touchDrag.y }}>
          Drop to move
        </div>
      )}
      <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
        <CalIcon className="w-4 h-4 text-[color:var(--mv-orange)]" />
        <strong className="text-[color:var(--mv-navy)] text-sm">{fmtDay(parseYMD(date))}</strong>
        <span className="text-xs text-slate-500">· {bookingsForDay.length} job{bookingsForDay.length === 1 ? '' : 's'}</span>
      </div>
      {dayIsEmpty && (
        <div className="px-4 py-5 bg-emerald-50 border-b border-emerald-200 text-center text-sm text-emerald-900 font-bold" data-testid={`cal-empty-${date}`}>
          No jobs scheduled for this day. Drop a booking here or tap <strong>+ New booking</strong> to create one.
        </div>
      )}
      <div className="overflow-x-auto">
        <div className="min-w-max flex">
          {/* Hour gutter */}
          <div className="w-14 shrink-0 border-r border-slate-100 pt-[60px] sm:pt-11">
            {HOURS.map(h => (
              <div key={h} className="text-[11px] font-bold text-slate-400 text-right pr-2" style={{ height: SLOT_HEIGHT_PX }}>{String(h).padStart(2, '0')}:00</div>
            ))}
          </div>
          {/* Driver columns */}
          <div className="flex-1 flex">
            {cols.map((c) => {
              const items = byDriver.get(c.id || '__u') || [];
              return (
                <div key={c.id || 'u'} className="flex-1 min-w-[180px] border-r border-slate-100 last:border-r-0">
                  <div className="px-2 h-16 sm:h-12 flex flex-col justify-center gap-0.5 border-b border-slate-100 bg-slate-50 sticky top-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {c.id ? <DriverChip d={c} size={18} /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                      <span className="text-xs font-bold text-[color:var(--mv-navy)] truncate" data-testid={`cal-col-name-${c.id || 'unassigned'}`}>{c.name}</span>
                    </div>
                    {c.id && dayMiles[c.id] && <div className="min-w-0"><DayMiles t={dayMiles[c.id]} testId={`day-miles-${c.id}-${date}`} /></div>}
                  </div>
                  <div
                    className="relative"
                    style={{ height: SLOT_HEIGHT_PX * gridRows }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleCellDrop(e, c.id)}
                    data-cal-col={c.id === null ? 'null' : c.id}
                    data-testid={`cal-col-${c.id || 'unassigned'}`}
                  >
                    {/* Hour lines */}
                    {HOURS.map((h, i) => (
                      <div key={h} className="absolute left-0 right-0 border-t border-slate-100" style={{ top: i * SLOT_HEIGHT_PX }}></div>
                    ))}
                    {preview && preview.col === (c.id === null ? 'null' : c.id) && (
                      <div className="absolute left-1 right-1 rounded-md border-2 border-dashed border-[color:var(--mv-navy)] bg-[color:var(--mv-navy)]/10 pointer-events-none z-20 flex items-start justify-end p-1" style={{ top: preview.top, height: preview.height }} data-testid="cal-drop-preview">
                        <span className="text-[11px] font-black bg-[color:var(--mv-navy)] text-white px-1.5 py-0.5 rounded">{preview.time}</span>
                      </div>
                    )}
                    {/* Booking blocks */}
                    {items.map((b) => {
                      const start = hmToMin(b.booking.time);
                      const dur = durationHours(b.hours_estimate) * 60;
                      const top = ((start - START_HOUR * 60) / 60) * SLOT_HEIGHT_PX;
                      const height = Math.max(40, (dur / 60) * SLOT_HEIGHT_PX - 2);
                      const driverColor = c.color || '#64748b';
                      const waste = isWasteJob(b);
                      const bg = waste ? WASTE_BG : AB_BG;
                      const textCls = waste ? 'text-white' : 'text-amber-950';
                      const overlap = overlapMap.get(b.id);
                      const dep = depositState(b);
                      const compact = dur < 60;
                      const price = `£${Number(b.booking?.final_price ?? b.price ?? 0).toLocaleString('en-GB')}`;
                      return (
                        <div
                          key={b.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, b, dur)}
                          onDragEnd={endDrag}
                          onTouchStart={(e) => startTouchDrag(e, b.id, dur)}
                          onClick={() => onBlockClick(b)}
                          className={`absolute left-1 right-1 rounded-md ${textCls} cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md hover:!h-auto hover:z-30 transition-[box-shadow,opacity] duration-150 ${compact ? 'px-1 py-0.5' : 'p-1.5'} leading-tight overflow-hidden touch-none select-none ${dragId === b.id ? 'opacity-40' : ''}`}
                          style={{
                            top,
                            height,
                            ...(laneMap.get(b.id)?.count > 1 ? {
                              left: `calc(${(laneMap.get(b.id).lane / laneMap.get(b.id).count) * 100}% + 2px)`,
                              width: `calc(${100 / laneMap.get(b.id).count}% - 4px)`,
                              right: 'auto',
                            } : {}),
                            background: bg,
                            borderLeft: c.id ? `4px solid ${driverColor}` : undefined,
                            border: overlap ? '2px solid #ef4444' : (c.id ? undefined : '1px solid rgba(0,0,0,0.1)'),
                          }}
                          title={`${b.ref} · ${b.name} · ${b.booking.time} · ${dep === 'verified' ? 'DEPOSIT PAID (CHECKED)' : dep === 'claimed' ? `CONFIRM BOOKING DEPOSIT · REF ${b.deposit_reference || '—'}` : 'DEPOSIT NOT PAID'}`}
                          data-testid={`cal-block-${b.ref}`}
                        >
                          {compact ? (
                            <div data-testid={`cal-block-compact-${b.ref}`}>
                              <div className="flex items-center gap-1 text-[10px]">
                                <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-white ${dep === 'verified' ? 'bg-emerald-500' : dep === 'claimed' ? 'bg-orange-700' : 'bg-red-500'}`} data-testid={`cal-deposit-${dep === 'verified' ? 'paid' : dep === 'claimed' ? 'claimed' : 'unpaid'}-${b.ref}`} />
                                <span className="font-black shrink-0">{b.booking.time}</span>
                                <span className="font-bold truncate flex-1 min-w-0">{b.name || '—'}</span>
                                {overlap && <AlertTriangle className="w-3 h-3 text-red-600 shrink-0" data-testid={`cal-overlap-${b.ref}`} />}
                                <span className={`font-black shrink-0 px-0.5 rounded ${waste ? 'bg-white/25' : 'bg-white/60'}`} data-testid={`cal-block-price-${b.ref}`}>{price}</span>
                              </div>
                              <div className="text-[9px] opacity-90 truncate"><span className="font-black uppercase">{waste ? 'Waste' : 'A-B'}</span> · {b.van_recommendation || serviceLabel(b.service) || '—'}{STATUS_BADGE[b.job_status] ? ` · ${STATUS_BADGE[b.job_status].label}` : ''}</div>
                              <div className="text-[9px] opacity-90 truncate" data-testid={`cal-block-from-${b.ref}`}>{b.from_postcode || addrLine(b, 'from') || '—'}{(b.to_postcode || b.to_address) ? ` → ${b.to_postcode || addrLine(b, 'to')}` : ''}</div>
                              {lateMap[b.id] && <LateBadge p={lateMap[b.id]} ref_={b.ref} />}
                            </div>
                          ) : (<>
                          <div className="flex items-center justify-between gap-1">
                            <div className="text-[10px] font-black opacity-90">{b.booking.time}</div>
                            <div className="flex items-center gap-1">
                              {overlap && (
                                <span className="inline-flex items-center gap-0.5 bg-red-600 text-white text-[9px] font-black uppercase px-1 py-0.5 rounded" data-testid={`cal-overlap-${b.ref}`}>
                                  <AlertTriangle className="w-3 h-3" />Clash
                                </span>
                              )}
                              {['manner', 'timing'].map(k => (tagOf(b, k) && b.customer_profile[k] !== 'calm' && b.customer_profile[k] !== 'flexible') ? (
                                <span key={k} className={`text-[9px] font-black uppercase px-1 py-0.5 rounded border ${tagOf(b, k).cls}`} data-testid={`cal-tag-${k}-${b.ref}`}>{tagOf(b, k).short}</span>
                              ) : null)}
                              {/* Deposit indicator — bright tick or cross for on-the-move staff */}
                              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${dep === 'verified' ? 'bg-emerald-500' : dep === 'claimed' ? 'bg-orange-700 ring-2 ring-white' : 'bg-red-500'} text-white shadow`} data-testid={`cal-deposit-${dep === 'verified' ? 'paid' : dep === 'claimed' ? 'claimed' : 'unpaid'}-${b.ref}`}>
                                {dep === 'verified' ? <CheckCircle className="w-3.5 h-3.5" strokeWidth={3} /> : dep === 'claimed' ? <span className="text-[11px] font-black">?</span> : <XCircle className="w-3.5 h-3.5" strokeWidth={3} />}
                              </span>
                              <span className={`text-[9px] font-black uppercase tracking-wider px-1 py-0.5 rounded ${waste ? 'bg-white/25' : 'bg-amber-800/15'}`}>{waste ? 'Waste' : 'A-B'}</span>
                            </div>
                          </div>
                          {lateMap[b.id] && <div className="mt-0.5"><LateBadge p={lateMap[b.id]} ref_={b.ref} /></div>}
                          {STATUS_BADGE[b.job_status] && (
                            <span className={`inline-block mt-0.5 text-[9px] font-black uppercase tracking-wider text-white px-1 py-0.5 rounded ${STATUS_BADGE[b.job_status].cls}`} data-testid={`cal-status-${b.ref}`}>{STATUS_BADGE[b.job_status].label}</span>
                          )}
                          <div className="flex items-center justify-between gap-1">
                            <div className="text-[11px] font-bold truncate">{b.name || '—'}</div>
                            <div className={`text-[11px] font-black shrink-0 px-1 rounded ${waste ? 'bg-white/25' : 'bg-white/60'}`} data-testid={`cal-block-price-${b.ref}`}>{price}</div>
                          </div>
                          <div className="text-[10px] opacity-90 truncate">{b.van_recommendation || serviceLabel(b.service) || '—'}</div>
                          {(b.from_address || b.from_house_number || b.to_address || b.to_house_number) ? (<>
                            <div className="text-[10px] opacity-90 truncate" title={addrLine(b, 'from')} data-testid={`cal-block-from-${b.ref}`}><span className="font-black">From</span> {addrLine(b, 'from') || '—'}</div>
                            {(b.to_postcode || b.to_address) && <div className="text-[10px] opacity-90 truncate" title={addrLine(b, 'to')} data-testid={`cal-block-to-${b.ref}`}><span className="font-black">To</span> {addrLine(b, 'to')}</div>}
                          </>) : <div className="text-[10px] opacity-80 truncate">{b.from_postcode} → {b.to_postcode}</div>}
                          </>)}
                        </div>
                      );
                    })}
                    {c.id && items.length > 0 && (
                      <TravelLegs jobs={items} startOf={calStart} durOf={calDur} toPx={calPx} homePc={dm.yard} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------- New-booking modal -------- */
const NewBookingModal = ({ drivers, defaults, onClose, authFetch, onCreated }) => {
  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    service: 'removal', van: '1 Man & Van', hours: '2 hours',
    from_postcode: '', to_postcode: '', from_address: '', to_address: '', from_house_number: '', to_house_number: '',
    price: '',
    date: defaults?.date || new Date().toISOString().slice(0, 10),
    time: defaults?.time || '09:00',
    driver_id: defaults?.driver_id || '',
    notes: '',
    deposit_paid: false,
    deposit_amount: '', porter_id: '',
    booked_on: new Date().toLocaleDateString('en-CA'),
    manner: null, age_group: null, timing: null,
  });
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [step, setStep] = useState(1);
  const [inv, setInv] = useState(EMPTY_INV);
  const [wastePhotos, setWastePhotos] = useState([]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (step === 1) { setStep(2); return; }
    if (saving) return;
    const price = Math.round(parseFloat(String(form.price).replace(/[£,\s]/g, '')));
    const missing = [!form.name.trim() && 'customer name', !form.phone.trim() && 'phone', Number.isNaN(price) && 'final price', !form.date && 'date', !form.time && 'time'].filter(Boolean);
    if (missing.length) { toast.error(`Please fill in: ${missing.join(', ')}`); return; }
    setSaving(true);
    try {
      const dep = parseInt(form.deposit_amount, 10);
      const payload = { ...form, price, driver_id: form.driver_id || null, email: form.email.trim() || null, deposit_amount: form.deposit_paid && !Number.isNaN(dep) ? dep : null, porter_id: form.porter_id || null, inventory: inv };
      const { data } = await authFetch('post', '/admin/booking', payload);
      const ok = photos.length ? await uploadJobPhotos(authFetch, data.id, photos) : 0;
      const wp = jobKind(form.service) === 'clearance' ? wastePhotos : [];
      const okW = wp.length ? await uploadJobPhotos(authFetch, data.id, wp, 'waste') : 0;
      toast.success(`Booking #${data.ref} created${ok + okW ? ` with ${ok + okW} photo${ok + okW > 1 ? 's' : ''}` : ''}.`);
      if (data.porter_warning) toast.warning(data.porter_warning);
      onCreated?.();
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? `Could not create booking: ${d.map(x => `${(x.loc || []).slice(-1)[0]} ${x.msg}`).join('; ')}` : errMsg(err, 'Could not create booking'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onClick={onClose} data-testid="new-booking-modal">
      <form noValidate onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-xl bg-white rounded-xl shadow-xl my-10">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
          <h3 className="font-display text-xl font-extrabold text-[color:var(--mv-navy)]">New booking <span className="ml-1 text-xs font-bold text-slate-500 align-middle" data-testid="nb-step-label">Step {step} of 2 · {step === 1 ? 'The job' : 'Customer, date & price'}</span></h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><XIcon className="w-5 h-5" /></button>
        </div>
        {step === 1 ? (
          <div className="p-5 space-y-4" data-testid="nb-step-1">
            <Field label="Service *">
              <select value={form.service} onChange={(e) => { set('service', e.target.value); setInv(EMPTY_INV); setWastePhotos([]); }} className={INPUT_CLS} data-testid="nb-service">
                {SERVICE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <JobInventoryFields service={form.service} value={inv} onChange={setInv} />
            {jobKind(form.service) === 'clearance' && (
              <Field label="Waste photos (optional)">
                <input type="file" accept="image/*,.heic,.heif" multiple onChange={(e) => setWastePhotos(Array.from(e.target.files || []).slice(0, 8))} className="block w-full text-sm file:mr-3 file:h-9 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--mv-orange)] file:text-white file:font-bold" data-testid="nb-waste-photos" />
                {wastePhotos.length > 0 && <div className="mt-1 text-xs text-slate-500" data-testid="nb-waste-photos-count">{wastePhotos.length} waste photo{wastePhotos.length > 1 ? 's' : ''} selected — uploaded when you create the booking</div>}
              </Field>
            )}
          </div>
        ) : (
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="nb-step-2">
          <Field label="Customer name *"><input required value={form.name} onChange={(e) => set('name', e.target.value)} className={INPUT_CLS} data-testid="nb-name" /></Field>
          <Field label="Phone *"><input required value={form.phone} onChange={(e) => set('phone', e.target.value)} className={INPUT_CLS} data-testid="nb-phone" /></Field>
          <Field label="Email" className="sm:col-span-2"><input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={INPUT_CLS} placeholder="Optional — needed to send booking email" data-testid="nb-email" /></Field>
          <Field label="Van">
            <select value={form.van} onChange={(e) => set('van', e.target.value)} className={INPUT_CLS} data-testid="nb-van">
              <option>1 Man & Van</option>
              <option>2 Man & Van</option>
              <option>Luton Van</option>
              <option>7.5 Tonne</option>
              <option>XLWB</option>
            </select>
          </Field>
          <Field label="Hours"><input value={form.hours} onChange={(e) => set('hours', e.target.value)} className={INPUT_CLS} placeholder="e.g. 2 hours" data-testid="nb-hours" /></Field>
          <Field label="Final price (£) *">
            <input required type="text" inputMode="decimal" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value, offerFrom: '' }))} className={INPUT_CLS} data-testid="nb-price" />
            {form.offerFrom ? (
              <div className="mt-1 text-[11px] font-bold text-emerald-700" data-testid="nb-phone-offer-applied">10% phone offer applied (was £{form.offerFrom}). <button type="button" className="underline" onClick={() => setForm(f => ({ ...f, price: f.offerFrom, offerFrom: '' }))}>Undo</button></div>
            ) : parseInt(form.price, 10) > 0 && (
              <button type="button" onClick={() => setForm(f => ({ ...f, offerFrom: f.price, price: String(Math.round(parseInt(f.price, 10) * 0.9)) }))} className="mt-1 text-[11px] font-bold text-[color:var(--mv-orange)] underline" data-testid="nb-phone-offer">
                Phone offer: take 10% off (£{form.price} → £{Math.round(parseInt(form.price, 10) * 0.9)}). Deposit is worked out from the new price.
              </button>
            )}
          </Field>
          <Field label="From postcode"><input value={form.from_postcode} onChange={(e) => set('from_postcode', e.target.value.toUpperCase())} className={INPUT_CLS} data-testid="nb-from" /></Field>
          <Field label="To postcode"><input value={form.to_postcode} onChange={(e) => set('to_postcode', e.target.value.toUpperCase())} className={INPUT_CLS} data-testid="nb-to" /></Field>
          <Field label="From address" className="sm:col-span-2">
            <AddressFinder postcode={form.from_postcode} house={form.from_house_number} address={form.from_address} onChange={({ house, address }) => setForm(f => ({ ...f, from_house_number: house, from_address: address }))} testPrefix="nb-from" inputCls={INPUT_CLS} />
          </Field>
          <Field label="To address" className="sm:col-span-2">
            <AddressFinder postcode={form.to_postcode} house={form.to_house_number} address={form.to_address} onChange={({ house, address }) => setForm(f => ({ ...f, to_house_number: house, to_address: address }))} testPrefix="nb-to" inputCls={INPUT_CLS} />
          </Field>
          <Field label="Date *"><input required type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className={INPUT_CLS} data-testid="nb-date" /></Field>
          <Field label="Time *"><input required type="time" value={form.time} onChange={(e) => set('time', e.target.value)} className={INPUT_CLS} data-testid="nb-time" /></Field>
          <Field label="When did they book it in? *"><input required type="date" value={form.booked_on} onChange={(e) => set('booked_on', e.target.value)} className={INPUT_CLS} data-testid="nb-booked-on" /></Field>
          <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="nb-customer-tags">
            <div className="text-xs font-black uppercase tracking-wider text-[color:var(--mv-navy)]">About the customer <span className="font-normal normal-case text-slate-500">(optional — tap again to clear)</span></div>
            {Object.entries(CUSTOMER_TAGS).map(([k, g]) => (
              <div key={k}>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{g.label}</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(g.opts).map(([v, o]) => (
                    <button key={v} type="button" onClick={() => set(k, form[k] === v ? null : v)} className={`h-10 px-3 rounded-md border text-xs font-bold transition-colors ${form[k] === v ? o.cls : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`} data-testid={`nb-${k}-${v}`}>{o.label}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Field label="Driver" className="sm:col-span-2">
            <select value={form.driver_id} onChange={(e) => set('driver_id', e.target.value)} className={INPUT_CLS} data-testid="nb-driver">
              <option value="">Unassigned</option>
              {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          <NbPorter form={form} set={set} authFetch={authFetch} />
          <Field label="Notes" className="sm:col-span-2"><textarea rows={2} maxLength={500} value={form.notes} onChange={(e) => set('notes', e.target.value)} className={INPUT_CLS} data-testid="nb-notes" /></Field>
          <label className="sm:col-span-2 inline-flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 cursor-pointer">
            <input type="checkbox" checked={form.deposit_paid} onChange={(e) => set('deposit_paid', e.target.checked)} data-testid="nb-deposit-paid" />
            <span><strong>Deposit received</strong> — e.g. the customer paid by bank transfer</span>
          </label>
          {form.deposit_paid && (
            <Field label="Deposit amount received (£)" className="sm:col-span-2"><input type="number" min="0" value={form.deposit_amount} onChange={(e) => set('deposit_amount', e.target.value)} className={INPUT_CLS} placeholder="Leave blank for the standard 20%" data-testid="nb-deposit-amount" /></Field>
          )}
          <Field label="Job photos" className="sm:col-span-2">
            <input type="file" accept="image/*,.heic,.heif" multiple onChange={(e) => setPhotos(Array.from(e.target.files || []).slice(0, 8))} className="block w-full text-sm file:mr-3 file:h-9 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--mv-navy)] file:text-white file:font-bold" data-testid="nb-photos" />
            {photos.length > 0 && <div className="mt-1 text-xs text-slate-500" data-testid="nb-photos-count">{photos.length} photo{photos.length > 1 ? 's' : ''} selected — uploaded when you create the booking</div>}
          </Field>
        </div>
        )}
        <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
          {step === 1
            ? <button type="button" onClick={onClose} className="h-10 px-4 rounded-md text-sm font-bold text-slate-600 hover:text-slate-900">Cancel</button>
            : <button type="button" onClick={() => setStep(1)} className="h-10 px-4 rounded-md text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="nb-back">← Back to job</button>}
          {step === 1 ? (
            <button key="nb-next" type="submit" className="h-10 px-5 rounded-md bg-[color:var(--mv-orange)] hover:bg-orange-600 text-white text-sm font-bold" data-testid="nb-next">Next: customer & date →</button>
          ) : (
          <button key="nb-submit" type="submit" disabled={saving} className="h-10 px-5 rounded-md bg-[color:var(--mv-navy)] hover:bg-[#13315c] text-white text-sm font-bold inline-flex items-center gap-2" data-testid="nb-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create booking
          </button>
          )}
        </div>
      </form>
    </div>
  );
};

export const CUSTOMER_TAGS = {
  manner: { label: 'How did they come across?', opts: {
    calm: { label: 'Calm', short: 'Calm', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    urgent: { label: 'Urgent / stressed', short: 'Urgent', cls: 'bg-red-600 text-white border-red-600' },
  } },
  age_group: { label: 'Do they sound…', opts: {
    young: { label: 'Young', short: 'Young', cls: 'bg-sky-100 text-sky-800 border-sky-300' },
    middle: { label: 'Middle-aged', short: 'Middle-aged', cls: 'bg-slate-200 text-slate-800 border-slate-300' },
    older: { label: 'Older / elderly', short: 'Elderly', cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  } },
  timing: { label: 'Time on the day', opts: {
    exact: { label: 'Needs the exact time', short: 'Exact time', cls: 'bg-orange-500 text-white border-orange-500' },
    flexible: { label: 'Flexible on the day', short: 'Flexible', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  } },
};
const tagOf = (b, k) => CUSTOMER_TAGS[k].opts[b?.customer_profile?.[k]];

const INPUT_CLS = 'w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)]';

const NbPorter = ({ form, set, authFetch }) => {
  const [res, setRes] = useState({ porters: [], day_porter_ids: [] });
  const { date, time, hours, driver_id: driverId } = form;
  const touched = React.useRef(false);
  useEffect(() => { touched.current = false; }, [date, driverId]);
  useEffect(() => {
    if (!date) return;
    let live = true;
    authFetch('get', `/admin/crew/available?date=${date}&time=${time || '09:00'}&hours=${encodeURIComponent(hours || '2')}${driverId ? `&driver_id=${driverId}` : ''}`)
      .then(({ data }) => { if (!live) return; setRes(data); if (!touched.current) set('porter_id', data.day_porter_ids[0] || ''); }).catch(() => {});
    return () => { live = false; };
  }, [authFetch, date, time, hours, driverId]); // eslint-disable-line react-hooks/exhaustive-deps
  const withDriver = res.porters.filter(p => p.with_driver).map(p => p.name);
  return (
    <Field label="Porter" className="sm:col-span-2">
      <select value={form.porter_id} onChange={(e) => { touched.current = true; set('porter_id', e.target.value); }} className={INPUT_CLS} data-testid="nb-porter">
        <option value="">No porter</option>
        {res.porters.map(p => <option key={p.id} value={p.id}>{p.name}{p.with_driver ? ' — already with this driver today' : ''}</option>)}
      </select>
      {withDriver.length > 0 && <div className="mt-1 text-xs text-emerald-700" data-testid="nb-porter-day-note">{withDriver.join(', ')} {withDriver.length > 1 ? 'are' : 'is'} with this driver that day and will be added to this job automatically.</div>}
    </Field>
  );
};

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);

/* -------- Main page -------- */
function CalendarInner() {
  const { user, authFetch, logout } = useAuth();
  const [range, setRange] = useState('day'); // day | week
  const [cursor, setCursor] = useState(() => toYMD(new Date()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [newBooking, setNewBooking] = useState(null); // null | { date, time, driver_id }
  const [pendingOpen, setPendingOpen] = useState(null);

  const days = range === 'week' ? 7 : 1;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const { data } = await authFetch('get', `/admin/calendar?start=${cursor}&days=${days}`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
      else toast.error("Couldn't load calendar.");
    } finally { setLoading(false); setRefreshing(false); }
  }, [authFetch, cursor, days, logout]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!pendingOpen || !data || !data.dates.includes(pendingOpen.date)) return;
    const found = data.bookings.find(b => b.id === pendingOpen.id);
    if (found) setSel(found);
    else toast.info(`#${pendingOpen.ref} is ${pendingOpen.status === 'booked' ? 'not shown on the calendar' : pendingOpen.status} — showing its day.`);
    setPendingOpen(null);
  }, [data, pendingOpen]);
  useEffect(() => { const t = setInterval(() => load(true), 60_000); return () => clearInterval(t); }, [load]);

  const shift = (deltaDays) => {
    const d = parseYMD(cursor);
    d.setDate(d.getDate() + deltaDays);
    setCursor(toYMD(d));
  };

  const jumpToday = () => setCursor(toYMD(new Date()));

  const handleDrop = async (quoteId, { driverId, date, time }) => {
    const prev = (data?.bookings || []).find(x => x.id === quoteId);
    if (!prev) return;
    const same = (prev.driver_id || null) === (driverId || null)
              && prev.booking?.date === date
              && prev.booking?.time === time;
    if (same) return;
    const dateChanged = prev.booking?.date !== date || prev.booking?.time !== time;
    // Optimistic UI
    setData(prevData => prevData ? {
      ...prevData,
      bookings: prevData.bookings.map(b => b.id === quoteId
        ? { ...b, driver_id: driverId, booking: { ...b.booking, date, time } }
        : b),
    } : prevData);
    try {
      if (dateChanged) {
        await withOverride((override_reason) => authFetch('patch', `/admin/estimate/${quoteId}/booking`, { date, time, keep_crew: true, driver_id: driverId || '', override_reason }));
      } else {
        await withOverride((override_reason) => authFetch('post', `/admin/estimate/${quoteId}/assign`, { driver_id: driverId || null, override_reason }));
      }

      // Offer to notify the customer about the change (only if date/time changed and we have their email)
      if (dateChanged && prev.email) {
        toast.success(`Moved to ${date} at ${time}`, {
          description: 'Email the customer about the change?',
          action: {
            label: 'Notify customer',
            onClick: async () => {
              try {
                await authFetch('post', `/admin/estimate/${quoteId}/notify-update`);
                toast.success('Customer notified by email.');
              } catch { toast.error("Couldn't send email."); }
            },
          },
          duration: 8000,
        });
      } else {
        toast.success(`Moved · ${date} ${time}`);
      }
      load(true);
    } catch (e) {
      if (!e.cancelled) toast.error(errMsg(e, "Couldn't move that booking."));
      load(true);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="flex items-center gap-3 text-slate-600"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" /> Loading calendar…</div></div>;
  }
  if (!data) return null;

  return (
    <div className="min-h-[60vh] bg-slate-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="h-9 w-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center justify-center" title="Back to dashboard"><ArrowLeft className="w-4 h-4 text-slate-600" /></Link>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-black text-[color:var(--mv-orange)]">Scheduler</div>
              <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Calendar</h1>
              <div className="text-xs text-slate-500">Drag any booking to a different driver or time · signed in as <strong>{user?.email}</strong></div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="cal-range-toggle">
              <button onClick={() => setRange('day')} className={`px-3 h-8 rounded text-xs font-bold inline-flex items-center gap-1 ${range === 'day' ? 'bg-[color:var(--mv-navy)] text-white' : 'text-slate-600'}`}><CalIcon className="w-3 h-3" />Day</button>
              <button onClick={() => setRange('week')} className={`px-3 h-8 rounded text-xs font-bold inline-flex items-center gap-1 ${range === 'week' ? 'bg-[color:var(--mv-navy)] text-white' : 'text-slate-600'}`}><LayoutGrid className="w-3 h-3" />Week</button>
            </div>
            <button onClick={() => shift(-days)} className="h-9 w-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center justify-center" title="Previous" data-testid="cal-prev"><ChevronLeft className="w-4 h-4 text-slate-600" /></button>
            <button onClick={jumpToday} className="h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-xs font-bold text-slate-700" data-testid="cal-today">Today</button>
            <button onClick={() => shift(days)} className="h-9 w-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center justify-center" title="Next" data-testid="cal-next"><ChevronRight className="w-4 h-4 text-slate-600" /></button>
            <div className="text-sm font-black text-[color:var(--mv-navy)] px-3 py-1.5 rounded-md bg-amber-100 border border-amber-200" data-testid="cal-date-label">
              {range === 'day' ? fmtDay(parseYMD(cursor)) : `${fmtDay(parseYMD(data.dates[0]))} – ${fmtDay(parseYMD(data.dates[data.dates.length - 1]))}`}
            </div>
            <MonthPicker authFetch={authFetch} value={cursor} onPick={(d) => { setRange('day'); setCursor(d); }} />
            <button onClick={() => load(true)} disabled={refreshing} className="h-9 w-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center justify-center" title="Refresh"><RefreshCw className={`w-4 h-4 text-slate-600 ${refreshing ? 'animate-spin' : ''}`} /></button>
            <button onClick={() => setNewBooking({ date: cursor, time: '09:00' })} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[color:var(--mv-orange)] hover:bg-orange-600 text-white text-xs font-bold" data-testid="cal-new-booking-btn">
              <Plus className="w-3.5 h-3.5" /> New booking
            </button>
          </div>
        </div>

        <BookingSearch authFetch={authFetch} onPick={(r) => { setRange('day'); setCursor(r.date); setPendingOpen(r); }} />

        {/* Legend */}
        <div className="flex items-center gap-4 text-[11px] text-slate-500 mb-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: WASTE_BG }}></span>Waste</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: AB_BG }}></span>A-B move</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-white"><CheckCircle className="w-3 h-3" strokeWidth={3} /></span>Deposit paid</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-700 text-white text-[10px] font-black">?</span>Confirm booking deposit</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white"><XCircle className="w-3 h-3" strokeWidth={3} /></span>Deposit not paid</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-2 border-red-500 bg-white"></span>Overlap</span>
          <span className="hidden md:inline">· Tap a block to toggle deposit · Drag (touch or mouse) to reschedule</span>
        </div>

        {/* Grids */}
        <div className="space-y-4">
          {data.dates.map(d => (
            <DayGrid
              key={d}
              date={d}
              drivers={data.drivers}
              bookings={data.bookings}
              onDrop={handleDrop}
              onBlockClick={setSel}
            />
          ))}
        </div>

        {data.bookings.length === 0 && (
          <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
            No bookings in this {range}. Try another date.
          </div>
        )}
      </div>

      {sel && (
        <QuickModal item={data.bookings.find(b => b.id === sel.id) || sel} onClose={() => setSel(null)} drivers={data.drivers} authFetch={authFetch} onAction={() => load(true)} />
      )}
      {newBooking && (
        <NewBookingModal
          drivers={data.drivers}
          defaults={newBooking}
          authFetch={authFetch}
          onClose={() => setNewBooking(null)}
          onCreated={() => load(true)}
        />
      )}
    </div>
  );
}

export default function AdminCalendar() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" /></div>;
  if (!user) return <LoginForm title="Move Van Ltd · Owner" subtitle="Sign in to view the scheduler." expectedRole="owner" />;
  if (user.role !== 'owner') return <div className="min-h-[60vh] flex items-center justify-center text-slate-600">Owners only.</div>;
  return <CalendarInner />;
}
